import React from 'react';
import { createPortal } from 'react-dom';
import { useModalScrollLock } from '../../hooks/useModalScrollLock';
import { ModalActionError } from '../../ui/ModalActionError';
import { CloseIcon } from '../../ui/CloseIcon';
import { captureOpener, holdFocusOnBackdrop, tabbableIn, trapTabKey } from '../receiving/dialogDom';
import './SubrentalActionDialog.css';

export type SubrentalActionDialogProps = {
  open: boolean;
  onClose: () => void;
  /** Small line above the title, e.g. "PXR0002002 · Queen City Audio Supply". */
  eyebrow?: string;
  title: string;
  subtitle?: React.ReactNode;
  /** `wide` for dialogs with line tables (Return, Plan the return, New subrental). */
  size?: 'standard' | 'wide';
  /** While saving the dialog cannot close (Escape and the X wait). */
  busy?: boolean;
  /** Shown above the footer; focused when it appears. */
  error?: string | null;
  errorTitle?: string;
  /** Muted note at the left of the footer. */
  footerNote?: React.ReactNode;
  /** The footer buttons (the primary one last). A `type="submit"` button submits the form. */
  footer: React.ReactNode;
  onSubmit?: () => void;
  /** Long forms (New subrental) turn this off so Enter in a field never submits them. */
  submitOnEnter?: boolean;
  className?: string;
  children: React.ReactNode;
};

/**
 * The frame for Subrental record dialogs (Return, Extend, Cancel incoming, Plan the return, New
 * subrental): the app's `.modal` header, body and footer, focus kept inside, Escape to close and
 * focus back on the button that opened it. A press on the backdrop never closes it.
 */
export default function SubrentalActionDialog(props: SubrentalActionDialogProps) {
  if (!props.open || typeof document === 'undefined') return null;
  return createPortal(<SubrentalActionDialogPanel {...props} />, document.body);
}

function SubrentalActionDialogPanel({
  onClose,
  eyebrow,
  title,
  subtitle,
  size = 'standard',
  busy = false,
  error,
  errorTitle,
  footerNote,
  footer,
  onSubmit,
  submitOnEnter = true,
  className,
  children,
}: SubrentalActionDialogProps) {
  const titleId = React.useId();
  const dialogRef = React.useRef<HTMLFormElement>(null);
  const headingRef = React.useRef<HTMLHeadingElement>(null);
  const bodyRef = React.useRef<HTMLDivElement>(null);
  const [opener] = React.useState(captureOpener);
  const busyRef = React.useRef(busy);
  busyRef.current = busy;
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;

  useModalScrollLock(true);

  // Focus starts on the first field (or the title) and returns to the opener on close.
  React.useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      const body = bodyRef.current;
      const field = body
        ? tabbableIn(body).find((element) => element.matches('input, select, textarea'))
        : undefined;
      (field ?? headingRef.current)?.focus({ preventScroll: true });
    });
    return () => {
      window.cancelAnimationFrame(frame);
      const active = document.activeElement;
      if (opener?.isConnected && (!active || active === document.body)) opener.focus({ preventScroll: true });
    };
  }, [opener]);

  const requestClose = React.useCallback(function requestClose() {
    if (!busyRef.current) onCloseRef.current();
  }, []);

  function handleKeyDown(event: React.KeyboardEvent<HTMLFormElement>) {
    const dialog = dialogRef.current;
    if (!dialog || !(event.target instanceof Node) || !dialog.contains(event.target)) return;
    if (event.key === 'Escape') {
      // A native select and an open picker handle their own Escape.
      if (event.defaultPrevented || document.activeElement instanceof HTMLSelectElement) return;
      event.preventDefault();
      event.stopPropagation();
      requestClose();
      return;
    }
    if (event.key === 'Enter' && !submitOnEnter && event.target instanceof HTMLInputElement) {
      // A text field's Enter would submit the whole form.
      event.preventDefault();
      return;
    }
    trapTabKey(event, dialog);
  }

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!busyRef.current) onSubmit?.();
  }

  return (
    <div className="modal-overlay subrental-action-overlay" onMouseDown={holdFocusOnBackdrop}>
      <form
        ref={dialogRef}
        className={['modal', 'subrental-action-dialog', size === 'wide' ? 'subrental-action-dialog--wide' : null, className]
          .filter(Boolean)
          .join(' ')}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-busy={busy || undefined}
        noValidate
        onKeyDown={handleKeyDown}
        onSubmit={handleSubmit}
      >
        <header className="modal__header subrental-action-dialog__header">
          <div className="subrental-action-dialog__heading">
            {eyebrow ? <p className="subrental-action-dialog__eyebrow">{eyebrow}</p> : null}
            <h2 ref={headingRef} id={titleId} className="modal__title subrental-action-dialog__title" tabIndex={-1}>
              {title}
            </h2>
            {subtitle ? <p className="subrental-action-dialog__subtitle">{subtitle}</p> : null}
          </div>
          <button type="button" className="btn-close" aria-label="Close" title="Close" onClick={requestClose} disabled={busy}>
            <CloseIcon />
          </button>
        </header>
        <div className="modal__body">
          <div ref={bodyRef} className="modal__body-content subrental-action-dialog__body">
            {children}
          </div>
        </div>
        <ModalActionError message={error ?? null} title={errorTitle} />
        <footer className="modal__footer subrental-action-dialog__footer">
          {footerNote ? <p className="subrental-action-dialog__note">{footerNote}</p> : null}
          <div className="subrental-action-dialog__buttons">{footer}</div>
        </footer>
      </form>
    </div>
  );
}
